"use client";

import type { PublicProfileResponse } from "@/features/profile/public/types/public-profile-data";

import { useParams } from "react-router-dom";
import { type ReactNode, useEffect, useState } from "react";
import { useDebouncedCallback } from "use-debounce";
import i18n from "i18next";

import { axiosApiInstance } from "@/libs/axios.tsx";
import { PUBLIC_PROFILE_ENDPOINTS } from "@/constants/api.ts";
import { usePublicProfileStore } from "@/features/profile/public/store/usePublicProfileStore";
import ProfileErrorPage from "@/features/profile/public/errorView/ProfileErrorPage";

interface GetPublicProfileDataWrapperProps {
  children: ReactNode;
}

export default function GetPublicProfileDataWrapper({
  children,
}: GetPublicProfileDataWrapperProps) {
  const { profileId, locale } = useParams();
  const { setProfileData, setIsLoading, setReviewsData, reviewPage } = usePublicProfileStore();
  const [errorStatus, setErrorStatus] = useState<number | null>(null);
  const [errorType, setErrorType] = useState<
    "not-found" | "server-error" | "unauthorized" | "generic"
  >("generic");

  const getPublicProfileData = async () => {
    if (!profileId) {
      setErrorType("not-found");
      setErrorStatus(404);
      setIsLoading(false);

      return;
    }

    try {
      setIsLoading(true);
      const { data } = await axiosApiInstance.get<PublicProfileResponse>(
        PUBLIC_PROFILE_ENDPOINTS(profileId).TUTOR.GET_PROFILE_DATA,
        { params: { locale_code: locale ?? i18n.language.toUpperCase() } },
      );
      const { data: reviewsData } = await axiosApiInstance(
        `${PUBLIC_PROFILE_ENDPOINTS(profileId ?? "").TUTOR.GET_REVIEWS}?per_page=20&page=${reviewPage}`,
      );

      setProfileData(data.data);
      setReviewsData(reviewsData);
    } catch (error: any) {
      const status = error.response?.status;

      setErrorStatus(status || 500);

      // Determine error type based on status code
      if (status === 404) {
        setErrorType("not-found");
      } else if (status >= 500) {
        setErrorType("server-error");
      } else if (status === 401 || status === 403) {
        setErrorType("unauthorized");
      } else {
        setErrorType("generic");
      }
    } finally {
      setTimeout(() => setIsLoading(false), 300);
    }
  };

  const debouncedGetData = useDebouncedCallback(() => {
    getPublicProfileData();
  }, 300);

  useEffect(() => {
    debouncedGetData();
  }, [profileId, locale]);

  if (errorStatus) {
    return <ProfileErrorPage errorType={errorType} statusCode={errorStatus || undefined} />;
  }

  return children;
}
